import { ref, reactive, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  CreateDeliveryTimeMutation,
  CreateDeliveryTimeMutationVariables,
  CreateDeliveryTimeDocument,
  UpdateDeliveryTimeMutation,
  UpdateDeliveryTimeMutationVariables,
  UpdateDeliveryTimeDocument,
  DeleteDeliveryTimeMutation,
  DeleteDeliveryTimeMutationVariables,
  DeleteDeliveryTimeDocument,
  CreateDeliveryGroupMutation,
  CreateDeliveryGroupMutationVariables,
  CreateDeliveryGroupDocument,
  UpdateDeliveryGroupMutation,
  UpdateDeliveryGroupMutationVariables,
  UpdateDeliveryGroupDocument,
  DeleteDeliveryGroupMutation,
  DeleteDeliveryGroupMutationVariables,
  DeleteDeliveryGroupDocument,
  IsDeliveryTimeQuery,
  IsDeliveryTimeQueryVariables,
  IsDeliveryTimeDocument,
  ListDeliveryTimeQuery,
  ListDeliveryTimeQueryVariables,
  ListDeliveryTimeDocument,
  GetDeliveryGroupQuery,
  GetDeliveryGroupQueryVariables,
  GetDeliveryGroupDocument,
  ListDeliveryGroupQuery,
  ListDeliveryGroupQueryVariables,
  ListDeliveryGroupDocument,
  ListDeliveryTimeAllQuery,
  ListDeliveryTimeAllQueryVariables,
  ListDeliveryTimeAllDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  GetDeliveryTimeQuery,
  GetDeliveryTimeQueryVariables,
  GetDeliveryTimeDocument,
  DeliveryTimeCommonInput,
  DeliveryTimeDeleteInput,
  DeliveryGroupCreateInput,
  DeliveryGroupUpdateInput,
  DeliveryGroupDeleteInput,
  DeliveryTimeGetInput,
  DeliveryTimeListInput,
  DeliveryTimeAllListInput,
  DeliveryGroupListInput,
  DeliveryGroupGetInput,
  DeliveryGroupAssignedId,
} from "@/generated/graphql";
import { ApolloQueryResult } from "@apollo/client/core";
import type {
  ListData,
  ListGroupDeliveryId,
  ListDataAreaDeliveryIdResist,
  ListDeliveryTimeItem,
  ListDeliveryTimeAllItem,
  ListGroupDeliveryIdItem,
  ChainSelectValue,
  ShopSelectValue,
  AreaSelectValue,
  DeliverySelectValue,
  DisplayDaysSettingValue,
  CapSelectValue,
  SelectTypeValue,
  DeliveryGroupSelectValue,
  AreaListItem,
  DeliveryGroupListItem,
} from "../types";
import { DELIVERY_ITEMS, GROUP_DELIVERY_ITEMS } from "../constants";

interface UseDeliverySettingApiParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  processFlag: Ref<number>;
  mode: Ref<string>;
  completeFlg: Ref<boolean>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  areaSelectValues: Ref<AreaSelectValue>;
  areaEditSelectValues: Ref<AreaSelectValue>;
  deliverySelectValues: Ref<DeliverySelectValue>;
  displayDaysSettingValues: Ref<DisplayDaysSettingValue>;
  selectTimeCapSelectValues: Ref<CapSelectValue>;
  selectGroupCapSelectValues: Ref<CapSelectValue>;
  selectTypeSelectValues: Ref<SelectTypeValue>;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  deliveryTimeCommonInput: DeliveryTimeCommonInput;
  deliveryTimeDeleteInput: DeliveryTimeDeleteInput;
  deliveryGroupCreateInput: DeliveryGroupCreateInput;
  deliveryGroupUpdateInput: DeliveryGroupUpdateInput;
  deliveryGroupDeleteInput: DeliveryGroupDeleteInput;
  deliveryTimeGetInput: DeliveryTimeGetInput;
  deliveryTimeListInput: DeliveryTimeListInput;
  deliveryTimeAllListInput: DeliveryTimeAllListInput;
  deliveryGroupListInput: DeliveryGroupListInput;
  deliveryGroupGetInput: DeliveryGroupGetInput;
  selectListCheckbox: Ref<ListDataAreaDeliveryIdResist[]>;
  listGroupDeliveryIdDataArray: Ref<ListGroupDeliveryId[]>;
  deliveryGroupListItems: Ref<DeliveryGroupListItem[]>;
  listDeliveryTimeAll: Ref<ListDeliveryTimeAllItem[]>;
  listDataAreaDeliveryIdResistArray: Ref<ListDataAreaDeliveryIdResist[]>;
  listGroupDeliveryId: Ref<ListGroupDeliveryIdItem[]>;
  areaListItems: Ref<AreaListItem[]>;
  use_list: { [key: string]: number };
  getAreaName: (area_code: string) => string;
  getShopName: (shop_code: string) => string;
  getDeliveryIdName: (delivery_id: number) => string;
}

/**
 * 画面で利用する API 呼び出し処理を提供する composable。
 *
 * @returns API 通信を行う関数群
 */
export const useDeliverySettingApi = ({
  errdialog,
  errMessage,
  overlay,
  processFlag,
  mode,
  completeFlg,
  chainSelectValues,
  shopSelectValues,
  areaSelectValues,
  areaEditSelectValues,
  deliverySelectValues,
  displayDaysSettingValues,
  selectTimeCapSelectValues,
  selectGroupCapSelectValues,
  selectTypeSelectValues,
  deliveryGroupSelectValues,
  deliveryTimeCommonInput,
  deliveryTimeDeleteInput,
  deliveryGroupCreateInput,
  deliveryGroupUpdateInput,
  deliveryGroupDeleteInput,
  deliveryTimeGetInput,
  deliveryTimeListInput,
  deliveryTimeAllListInput,
  deliveryGroupListInput,
  deliveryGroupGetInput,
  selectListCheckbox,
  listGroupDeliveryIdDataArray,
  deliveryGroupListItems,
  listDeliveryTimeAll,
  listDataAreaDeliveryIdResistArray,
  listGroupDeliveryId,
  areaListItems,
  use_list,
  getAreaName,
  getShopName,
  getDeliveryIdName,
}: UseDeliverySettingApiParams) => {
  // Mutation result refs
  const createDeliveryTimeMutation = ref<CreateDeliveryTimeMutation | null>(null);
  const createDeliveryGroupMutation = ref<CreateDeliveryGroupMutation | null>(null);
  const updateDeliveryGroupMutation = ref<UpdateDeliveryGroupMutation | null>(null);
  let deliveryUseListCacheKey = "";

  const clearDeliveryUseList = (): void => {
    Object.keys(use_list).forEach((key) => delete use_list[key]);
    deliveryUseListCacheKey = "";
  };

  /**
   * 登録・更新・削除処理
   */
  const createDeliveryTime = async (): Promise<void> => {
    // graphql接続
    const apolloClient = await generateApolloClient();

    // キャップ設定で選択されていないほうはnullをセット
    if (selectTimeCapSelectValues.value.index === 0) {
      deliveryTimeCommonInput.delivery_cap_mon = null;
      deliveryTimeCommonInput.delivery_cap_tue = null;
      deliveryTimeCommonInput.delivery_cap_wed = null;
      deliveryTimeCommonInput.delivery_cap_thu = null;
      deliveryTimeCommonInput.delivery_cap_fri = null;
      deliveryTimeCommonInput.delivery_cap_sat = null;
      deliveryTimeCommonInput.delivery_cap_sun = null;
    } else {
      deliveryTimeCommonInput.max_deliverable_unit = null;
    }

    if (processFlag.value === 0) {
      // 登録
      const result = await apolloClient.mutate<
        CreateDeliveryTimeMutation,
        CreateDeliveryTimeMutationVariables
      >({
        mutation: CreateDeliveryTimeDocument,
        variables: { input: deliveryTimeCommonInput },
        fetchPolicy: "no-cache",
      });
      createDeliveryTimeMutation.value = result.data;
      if (
        createDeliveryTimeMutation.value!.createDeliveryTime!.Result!.result ===
          "Failed" &&
        createDeliveryTimeMutation.value!.createDeliveryTime!.Result!.message!.length > 0
      ) {
        errdialog.value = true;
        errMessage.value =
          createDeliveryTimeMutation.value!.createDeliveryTime!.Result.message ?? "";
        return;
      }
    } else if (processFlag.value === 1) {
      // 更新
      const result = await apolloClient.mutate<
        UpdateDeliveryTimeMutation,
        UpdateDeliveryTimeMutationVariables
      >({
        mutation: UpdateDeliveryTimeDocument,
        variables: { input: deliveryTimeCommonInput },
        fetchPolicy: "no-cache",
      });
      createDeliveryTimeMutation.value = result.data;
    } else {
      deliveryTimeDeleteInput.delivery_id = deliveryTimeCommonInput.delivery_id;
      deliveryTimeDeleteInput.area_code = deliveryTimeCommonInput.area_code;
      // 削除
      const result = await apolloClient.mutate<
        DeleteDeliveryTimeMutation,
        DeleteDeliveryTimeMutationVariables
      >({
        mutation: DeleteDeliveryTimeDocument,
        variables: { input: deliveryTimeDeleteInput },
        fetchPolicy: "no-cache",
      });
      createDeliveryTimeMutation.value = result.data;
    }
  };

  /**
   * 配達グループ登録・更新・削除処理
   */
  const createDeliveryGroup = async (): Promise<void> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // キャップ設定で未選択の方はnullをセット
    if (selectGroupCapSelectValues.value.index === 0) {
      deliveryGroupCreateInput.group_cap_mon = null;
      deliveryGroupCreateInput.group_cap_tue = null;
      deliveryGroupCreateInput.group_cap_wed = null;
      deliveryGroupCreateInput.group_cap_thu = null;
      deliveryGroupCreateInput.group_cap_fri = null;
      deliveryGroupCreateInput.group_cap_sat = null;
      deliveryGroupCreateInput.group_cap_sun = null;
    } else {
      deliveryGroupCreateInput.max_deliverable_unit = null;
    }

    if (processFlag.value === 0) {
      // 登録
      const result = await apolloClient.mutate<
        CreateDeliveryGroupMutation,
        CreateDeliveryGroupMutationVariables
      >({
        mutation: CreateDeliveryGroupDocument,
        variables: { input: deliveryGroupCreateInput },
        fetchPolicy: "no-cache",
      });
      createDeliveryGroupMutation.value = result.data;

      if (
        createDeliveryGroupMutation.value!.createDeliveryGroup!.Result!.result ===
          "Failed" &&
        createDeliveryGroupMutation.value!.createDeliveryGroup!.Result!.message!.length! > 0
      ) {
        errdialog.value = true;
        errMessage.value =
          createDeliveryGroupMutation.value!.createDeliveryGroup!.Result.message;
        return;
      } else {
        // 登録成功で初期化
        deliveryGroupCreateInput.assigned_id_list = [];
        deliveryGroupCreateInput.chain_code = "";
        deliveryGroupCreateInput.group_code = "";
        deliveryGroupCreateInput.group_delivery_id = 0;
        deliveryGroupCreateInput.name = "";
        completeFlg.value = true;
        clearDeliveryUseList();
        // グループリスト再取得
        await getGroupList();
      }
    } else if (processFlag.value === 1) {
      // キャップ設定で未選択の方はnullをセット
      if (selectGroupCapSelectValues.value.index === 0) {
        deliveryGroupUpdateInput.group_cap_mon = null;
        deliveryGroupUpdateInput.group_cap_tue = null;
        deliveryGroupUpdateInput.group_cap_wed = null;
        deliveryGroupUpdateInput.group_cap_thu = null;
        deliveryGroupUpdateInput.group_cap_fri = null;
        deliveryGroupUpdateInput.group_cap_sat = null;
        deliveryGroupUpdateInput.group_cap_sun = null;
        deliveryGroupUpdateInput.max_deliverable_unit =
          deliveryGroupCreateInput.max_deliverable_unit;
      } else {
        deliveryGroupUpdateInput.max_deliverable_unit = null;
        deliveryGroupUpdateInput.group_cap_mon =
          deliveryGroupCreateInput.group_cap_mon;
        deliveryGroupUpdateInput.group_cap_tue =
          deliveryGroupCreateInput.group_cap_tue;
        deliveryGroupUpdateInput.group_cap_wed =
          deliveryGroupCreateInput.group_cap_wed;
        deliveryGroupUpdateInput.group_cap_thu =
          deliveryGroupCreateInput.group_cap_thu;
        deliveryGroupUpdateInput.group_cap_fri =
          deliveryGroupCreateInput.group_cap_fri;
        deliveryGroupUpdateInput.group_cap_sat =
          deliveryGroupCreateInput.group_cap_sat;
        deliveryGroupUpdateInput.group_cap_sun =
          deliveryGroupCreateInput.group_cap_sun;
      }

      // 更新
      const result = await apolloClient.mutate<
        UpdateDeliveryGroupMutation,
        UpdateDeliveryGroupMutationVariables
      >({
        mutation: UpdateDeliveryGroupDocument,
        variables: { input: deliveryGroupUpdateInput },
        fetchPolicy: "no-cache",
      });
      updateDeliveryGroupMutation.value = result.data;

      if (
        updateDeliveryGroupMutation.value!.updateDeliveryGroup!.Result!.result ===
          "Failed" &&
        updateDeliveryGroupMutation.value!.updateDeliveryGroup!.Result!.message!.length > 0
      ) {
        errdialog.value = true;
        errMessage.value =
          updateDeliveryGroupMutation.value!.updateDeliveryGroup!.Result.message;
        return;
      }
      clearDeliveryUseList();
    } else {
      // 削除
      try {
        const result = await apolloClient.mutate<
          DeleteDeliveryGroupMutation,
          DeleteDeliveryGroupMutationVariables
        >({
          mutation: DeleteDeliveryGroupDocument,
          variables: { input: deliveryGroupDeleteInput },
          fetchPolicy: "no-cache",
        });
        createDeliveryGroupMutation.value = result.data;

        if (listGroupDeliveryIdDataArray.value.length === 1) {
          listGroupDeliveryIdDataArray.value = [];

          // グループリスト再取得
          await getGroupList();
        }
        clearDeliveryUseList();
      } catch (err) {
        console.error(err);
        return;
      }
    }
  };

  /**
   * 配達便存在チェック
   */
  const chkDeliveryId = async (): Promise<boolean> => {
    try {
      const apolloClient = await generateApolloClient();
      deliveryTimeGetInput.chain_code = deliveryTimeCommonInput.chain_code;
      deliveryTimeGetInput.shop_code = deliveryTimeCommonInput.shop_code;
      deliveryTimeGetInput.area_code = areaEditSelectValues.value.area_code;
      deliveryTimeGetInput.delivery_id = deliverySelectValues.value.delivery_id;

      // 配達便が存在しているかチェック
      const countDeliveryId = await apolloClient.query<IsDeliveryTimeQuery, IsDeliveryTimeQueryVariables>({
        query: IsDeliveryTimeDocument,
        variables: { input: deliveryTimeGetInput },
      });

      //既に存在している場合エラー
      if (countDeliveryId.data.isDeliveryTime?.length !== 0) {
        errdialog.value = true;
        errMessage.value =
          `配達便 ${deliverySelectValues.value.delivery_id} 便は既に存在しています。`;
        deliveryTimeCommonInput.delivery_id = 0;
        return false;
      }
      return true;
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "予期しないエラーが発生しました。";
      }
      errdialog.value = true;
      return false;
    }
  };

  /**
   * 配達便都度便存在チェック
   */
  const chkDeliveryIdType = async (): Promise<boolean | undefined> => {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      deliveryTimeGetInput.chain_code = deliveryTimeCommonInput.chain_code;
      deliveryTimeGetInput.shop_code = deliveryTimeCommonInput.shop_code;
      deliveryTimeGetInput.area_code = areaEditSelectValues.value.area_code;
      deliveryTimeGetInput.delivery_id = deliverySelectValues.value.delivery_id;

      if (deliverySelectValues.value.delivery_id == 0) {
        // 配達時間一覧取得 [引数:input参照]
        deliveryTimeListInput.area_code = [areaEditSelectValues.value.area_code];
        deliveryTimeListInput.shop_code = deliveryTimeCommonInput.shop_code;
        deliveryTimeListInput.chain_code = deliveryTimeCommonInput.chain_code;
        let deliveryTime: ApolloQueryResult<ListDeliveryTimeQuery>;
        try {
          deliveryTime = await apolloClient.query<ListDeliveryTimeQuery, ListDeliveryTimeQueryVariables>({
            query: ListDeliveryTimeDocument,
            variables: { input: deliveryTimeListInput },
            fetchPolicy: "no-cache",
          });

          if (deliveryTime.data.listDeliveryTime!.length > 1) {
            errdialog.value = true;
            errMessage.value = "選択されたエリアは都度配達ではありません。";
            return false;
          } else if (deliveryTime.data.listDeliveryTime!.length == 1) {
            if (
              deliveryTime.data.listDeliveryTime !== undefined &&
              deliveryTime.data.listDeliveryTime !== null &&
              deliveryTime.data.listDeliveryTime[0] !== undefined &&
              deliveryTime.data.listDeliveryTime[0] !== null &&
              deliveryTime.data.listDeliveryTime[0].delivery_id == 0
            ) {
              errdialog.value = true;
              errMessage.value =
                "選択されたエリアは既に都度配達として設定されています。";
              return false;
            } else {
              errdialog.value = true;
              errMessage.value = "選択されたエリアは都度配達ではありません。";
              return false;
            }
          } else {
            return true;
          }
        } catch (err) {
          console.error(err);
          return;
        }
      } else {
        try {
          // 配達便が存在しているかチェック
          deliveryTimeGetInput.delivery_id = 0;
          const countDeliveryIdType = await apolloClient.query<IsDeliveryTimeQuery, IsDeliveryTimeQueryVariables>({
            query: IsDeliveryTimeDocument,
            variables: { input: deliveryTimeGetInput },
          });

          // 既に存在している場合エラー
          if (countDeliveryIdType.data.isDeliveryTime?.length !== 0) {
            errdialog.value = true;
            errMessage.value =
              "選択されたエリアは都度配達として設定されています。";
            return false;
          } else {
            return true;
          }
        } catch (err) {
          console.error(err);
          return;
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "予期しないエラーが発生しました。";
      }

      errdialog.value = true;
      return false;
    }
  };

  /**
   * グループ便存在チェック
   */
  const chkGroupDeliveryId = async (): Promise<boolean> => {
    //graphql接続
    const apolloClient = await generateApolloClient();
    deliveryGroupGetInput.chain_code = chainSelectValues.value.chain_code;
    deliveryGroupGetInput.group_code = deliveryGroupSelectValues.value.group_code;
    deliveryGroupGetInput.group_delivery_id = deliverySelectValues.value
      ? deliverySelectValues.value.delivery_id
      : 0;
    try {
      const groupDeliveryid = await apolloClient.query<
        GetDeliveryGroupQuery,
        GetDeliveryGroupQueryVariables
      >({
        query: GetDeliveryGroupDocument,
        variables: { input: deliveryGroupGetInput },
        fetchPolicy: "no-cache",
      });
      //既に存在している場合エラー
      if (groupDeliveryid.data.getDeliveryGroup!.response !== null) {
        errdialog.value = true;
        errMessage.value =
          "グループ便 " +
          deliverySelectValues.value.delivery_id +
          " 便は既に存在しています。";
        return false;
      } else {
        return true;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "予期しないエラーが発生しました。";
      }
      errdialog.value = true;
      return false;
    }
  };

  /**
   * グループリスト取得
   */
  const getGroupList = async (): Promise<void> => {
    // graphql接続
    const apolloClient = await generateApolloClient();
    try {
      deliveryGroupListInput.chain_code = chainSelectValues.value.chain_code;
      const listDeliveryGroup = await apolloClient.query<
        ListDeliveryGroupQuery,
        ListDeliveryGroupQueryVariables
      >({
        query: ListDeliveryGroupDocument,
        variables: { input: deliveryGroupListInput },
        fetchPolicy: "no-cache",
      });
      deliveryGroupListItems.value = listDeliveryGroup.data.listDeliveryGroup;
    } catch (err) {
      console.error(err);
      return;
    }
  };

  /**
   * エリア・便重複チェック用
   */
  const getDelivery = async (): Promise<void> => {
    //graphql接続
    const apolloClient = await generateApolloClient();
    const groupListCacheKey = [
      chainSelectValues.value.chain_code,
      ...(deliveryGroupListItems.value ?? []).map(
        (value) => `${value?.chain_code ?? ""}:${value?.group_code ?? ""}`
      ),
    ].join("|");

    if (deliveryUseListCacheKey === groupListCacheKey) return;

    clearDeliveryUseList();

    if (
      deliveryGroupListItems.value !== null &&
      deliveryGroupListItems.value !== undefined
    ) {
      await Promise.all(
        deliveryGroupListItems.value.map(async (value) => {
          try {
            const groupDeliveryGetInput = {
              customer_code: deliveryGroupGetInput.customer_code,
              group_code: value ? value.group_code : "",
              chain_code: value ? value.chain_code : "",
            };
            const groupDeliveryid = await apolloClient.query<
              GetDeliveryGroupQuery,
              GetDeliveryGroupQueryVariables
            >({
              query: GetDeliveryGroupDocument,
              variables: {
                input: groupDeliveryGetInput,
              },
              fetchPolicy: "no-cache",
            });

            const groupDeliveryList =
              groupDeliveryid.data.getDeliveryGroup?.response ?? [];
            if (
              groupDeliveryList !== null &&
              groupDeliveryList !== undefined &&
              groupDeliveryList.length > 0
            ) {
              listGroupDeliveryId.value = groupDeliveryList;
            }

            if (
              groupDeliveryList !== null &&
              groupDeliveryList !== undefined
            ) {
              groupDeliveryList.forEach((_value) => {
                if (!_value) return;
                _value?.assigned_id_list?.forEach(
                  (__value: DeliveryGroupAssignedId) => {
                    const _use_key =
                      __value.shop_code +
                      __value.area_code +
                      __value.delivery_id;
                    use_list[_use_key] = 1;
                  }
                );
              });
            }
          } catch (err) {
            // getDeliveryGroupがエラーを返す場合はスキップ（MySQL接続制限等）
            console.warn("配達グループの取得に失敗しました:", err);
          }
        })
      );
    }

    deliveryUseListCacheKey = groupListCacheKey;
  };

  return {
    createDeliveryTimeMutation,
    createDeliveryGroupMutation,
    updateDeliveryGroupMutation,
    createDeliveryTime,
    createDeliveryGroup,
    chkDeliveryId,
    chkDeliveryIdType,
    chkGroupDeliveryId,
    getGroupList,
    getDelivery,
  };
};
